import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  approveTimesheet,
  bulkApproveTimesheets,
  disputeTimesheet,
  listMyTimesheets,
  listVenueTimesheets,
  manualCheckOut,
} from "@/features/timesheets/api/timesheetsApi";
import type { TimesheetDisputePayload, TimesheetStatus } from "@/features/timesheets/types";

const TIMESHEETS_KEYS = {
  venue: (status?: TimesheetStatus) => ["timesheets", "venue", status ?? "all"] as const,
  me: ["timesheets", "me"] as const,
};

export function useVenueTimesheets(status?: TimesheetStatus) {
  return useQuery({
    queryKey: TIMESHEETS_KEYS.venue(status),
    queryFn: () => listVenueTimesheets(status),
  });
}

export function useMyTimesheets() {
  return useQuery({
    queryKey: TIMESHEETS_KEYS.me,
    queryFn: listMyTimesheets,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["timesheets"] });
  // earnings reads from timesheets — keep it in sync.
  qc.invalidateQueries({ queryKey: ["earnings"] });
  // An approved timesheet flips a shift into the ops "Worker payouts"
  // queue; a dispute can flip it back. Keep that view fresh.
  qc.invalidateQueries({ queryKey: ["ops", "payouts"] });
}

export function useApproveTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveTimesheet(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDisputeTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TimesheetDisputePayload }) =>
      disputeTimesheet(id, payload),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useBulkApproveTimesheets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => bulkApproveTimesheets(ids),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useManualCheckOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shiftId: string) => manualCheckOut(shiftId),
    onSuccess: (_, shiftId) => {
      qc.invalidateQueries({ queryKey: ["shift-events", shiftId] });
      qc.invalidateQueries({ queryKey: ["shifts"] });
      invalidateAll(qc);
    },
  });
}
